import { Deserializer } from 'jsonapi-serializer';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { Company } from '../models/Company';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { CompanyWrapper } from '../models/wrappers/CompanyWrapper';
import { APIConnector } from './api/APIConnector';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPagination } from './utilities/paginationBuilder';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();

export const deleteCompany = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `company/${id}`;
        const company = {
            data: {
                type: "company",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, company);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCompanyNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const companyFields = "fields[company]=compName,isDefault,site";
        const siteFields = "fields[site]=siteName";
        const url = `company?${paginationString}&include=site&${companyFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, company: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const companies: Name[] = company.map((c): Name => {
                    return {
                        value: c['id'],
                        label: c['comp-name'],
                        optionalColumns: [{ key: 'site', value: c['site']?.['site-name'] },],
                        isDefault: c['is-default']
                    };
                });

                return resolve({ rows: companies, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
}

export const getCompanyNames = async (): Promise<Name[]> => {
    try {
        const url = 'company?fields[company]=compName';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, company: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const companies: Name[] = company.map((c): Name => {
                    return {
                        value: c['id'],
                        label: c['comp-name']
                    };
                });

                return resolve(companies);
            })
        })
    } catch(error){
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
}

export const getCompanies = async (listParameters?: ListQuery): Promise<CompanyWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const fieldsCompany = "&fields[company]=site,isDefault,compName,totalMasters";
        const fieldsSites = "&fields[site]=siteName";
        const url = 'company?include=site' + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + fieldsCompany + fieldsSites;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiCompanies: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const companies: Company[] = apiCompanies.map((c): Company => {
                    return {
                        id: c['id'],
                        name: c['comp-name'],
                        site: c['site']['site-name'],
                        enrolments: c['total-masters'],
                        siteId: +c['site']['id'],
                        default: c['comp-default'],
                        isDefault: c['is-default']
                    }
                })
                return resolve({ rows: companies, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCompanyById = async (id: string, listParameters?: ListQuery): Promise<Company> => {
    try{
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = (filter === '' ? `&filter=id==${id}` : `${filter};id==${id}`);
        const companyFields = "&fields[company]=site,compDefault,isDefault,compName,totalMasters";
        const siteFields = "&fields[site]=siteName";
        const url = "company?include=site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + companyFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const company: Company = {
                    id: data[0]['id'],
                    name: data[0]['comp-name'],
                    site: data[0]['site']['site-name'],
                    enrolments: data[0]['total-masters'],
                    siteId: data[0]['site']['id'],
                    default: data[0]['comp-default']
                };

                return resolve(company);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeCompany = async (company: Company, createCompany = true) :Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'company',
                id: company.id,
                attributes: {
                    compDefault: company.default ?? 0,
                    compName: company.name,
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: company.siteId
                        }
                    }
                }
            }
        }

        const url = `company${createCompany ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createCompany, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createCompany) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: content.errors.map(error => getConstraintViolation(error.detail)) })
                    }

                    new Deserializer().deserialize(content, (error, newCompany) => {
                        if (error) {
                            return reject({status: response.status, id: newCompany.id, success: false, message: genericError });
                        }
                        return resolve({ id: newCompany.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: company.id, success: true })
                }

                const content = await response.json();
                return reject({status: response.status, id: company.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({status: INTERNAL_SERVER_ERROR, id: company.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

interface MasterCompanyData {
    data: {
        type: string;
        id: string;
        relationships: {
            company: {
                data: {
                    type: string;
                    id: string;
                };
            };
        };
    };
}

interface RemoveMasterCompanyData {
    data: {
        type: string;
        id: string;
        relationships: {
            company: {
                // Define the properties of the "company" relationship here
            };
        };
    };
}

export const buildMasterCompanyData = (
    assignMasterId: string[],
    companyId
): MasterCompanyData[] => {
    return assignMasterId.map((masterId) => {
        return {
            data: {
                type: "master",
                id: masterId,
                relationships: {
                    company: {
                        data: {
                            type: "company",
                            id: companyId,
                        },
                    },
                },
            },
        };
    });
};

export const buildMasterCompanyDataRemove = (unAssignMasterIds: string[]) => {
    return unAssignMasterIds.map((unAssignMasterId) => {
        return {
            data: {
                type: "master",
                id: unAssignMasterId,
                relationships: {
                    company: {},
                },
            },
        };
    });
};


export const assignUnassignMaterCompany = (
    assignMasterId: string[],
    unAssignedMasterId: string[],
    companyId: string
) => {
    const newMasterCompanies = buildMasterCompanyData(
        assignMasterId,
        companyId
    );

    const removeCompanyMasterDetail =
        buildMasterCompanyDataRemove(unAssignedMasterId);

    const addMasterCompanies = newMasterCompanies.map(
        async (newMasterCompany) => {
            return await editMasterCompany(
                newMasterCompany,
                newMasterCompany.data.id
            );
        }
    );

    const removeMasterCompanies = removeCompanyMasterDetail.map(
        async (unAssigned) => {
            return await removeMasterCompany(unAssigned, unAssigned.data.id);
        }
    );

    return Promise.allSettled([
        ...addMasterCompanies,
        ...removeMasterCompanies,
    ]);
};

export const editMasterCompany = async (
    newMasterCompany: MasterCompanyData,
    masterId
) => {
    try {
        const url = `master/${masterId}`;
        let response: Response;

        await apiConnector
            .sendData(url, false, newMasterCompany)
            .then(async (data) => {
                response = data;
            });
    } catch (error) {
        return new Promise(async (resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const removeMasterCompany = async (
    removeMasterCompany: RemoveMasterCompanyData,
    masterId
) => {
    try {
        const url = `master/${masterId}`;
        let response: Response;

        await apiConnector
            .sendData(url, false, removeMasterCompany)
            .then(async (data) => {
                response = data;
            });
    } catch (error) {
        return new Promise(async (resolve, reject) => {
            reject(processServerError(error));
        });
    }
};
